// The seat-role catalogs — `GET /me/seat-roles`. The ONE place the SPA learns what a role key is CALLED.
//
// It replaced four hand-maintained label maps that gave THREE different answers for the same key:
// `ORG_ROLE_CATALOG` + `orgRoleLabel` in `useOrgTeam.ts` (disagreeing with the seeded label on 22 of 25
// keys, and shipping two strings a backend test exists to forbid), a local `roleLabel` in `OrgTeamScreen`
// that did not title-case at all, `humanizeRole` in `OrgChip` that never consulted a catalog, and the band
// map in `shells/mobile/accountRoles.ts`. The org list also omitted the three bare-root keys
// (`label`/`manager`/`booking`) that `core.create_org` gives EVERY org founder — so an admin's own row on
// the Team page printed the raw lowercase key.
//
// PERSON-scoped, not band- or org-scoped: neither catalog has a tenant dimension, and the surface that needs
// them most (`CrewProfileModal`, the person dialog) mounts with no band and no org. See the server's
// `SeatRoleCatalogRead` doc for the full reasoning.
import { useQuery } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";

type SeatRoleCatalogRead = components["schemas"]["SeatRoleCatalogRead"];
type SeatRoleOption = components["schemas"]["SeatRoleOption"];

export type { SeatRoleCatalogRead, SeatRoleOption };

/**
 * Both role catalogs. `staleTime: Infinity` because the rows change only by migration, and the key is
 * person-scoped so it survives a band switch (which scorches `['band', …]`) and an artist switch.
 */
export function useSeatRoles() {
  const { api } = useApp();
  return useQuery({
    queryKey: ["me", "seat-roles"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/seat-roles", {});
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: Infinity,
  });
}

/**
 * Title-case a role key as a last resort. A seat rendering NO role is worse than an unpolished one: the
 * catalog is fetched, so it can be absent on first paint, and it can carry a key this build has not seen.
 */
function humanize(role: string): string {
  return role
    .split("_")
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/**
 * Display label for a role key, resolved against one catalog list.
 *
 * Callers pass the list matching the key's REALM — org keys against `orgRoles`, band keys against
 * `bandRoles`. The two vocabularies are disjoint but not visibly so, and running a key through the wrong
 * list title-cases it into something plausible and wrong; that is the mistake `PersonBandView.access`
 * already exists to prevent, so the payload keeps them separate rather than merging.
 */
export function roleLabelFrom(
  roles: readonly SeatRoleOption[] | null | undefined,
  role: string | null | undefined,
): string {
  if (!role) return "";
  const hit = roles?.find((r) => r.role === role);
  return hit?.label || humanize(role);
}

/**
 * The PLAIN-TEXT label for a role PICKER option (ART-171).
 *
 * The everywhere label of the full-access band title is just "Band Member" (seed 861) — the crimson AV mark
 * carries "full access", and `RoleLabel` renders it. But a picker `<option>` cannot hold that mark, and its
 * `label` is the accessible name AND the type-ahead search key, so two "Band Member" titles (full-access and
 * limited) would be indistinguishable there. This adds "(Full Access)" for the full-access one — the visible
 * twin of `RoleLabel`'s `spellFullAccess`, which every picker pairs it with so the shown row and its
 * accessible name agree.
 *
 * Keyed on `roleKey === "band_member_av"` and not owner-only, which in a picker (owner-only is filtered out)
 * is exactly the `band_member_full` title.
 */
export function pickerRoleLabel(option: SeatRoleOption): string {
  const base = option.label ?? "";
  return option.roleKey === "band_member_av" && !option.isOwnerOnly ? `${base} (Full Access)` : base;
}

/**
 * The role options assignable in an org of the given type — the invite picker's list. A RETIRED title
 * (`isRetired`, ART-359) is never offered; it stays in the catalog so a seat holding one keeps its label.
 *
 * The catalog is served whole rather than pre-filtered because a LABEL has to resolve for any stored key,
 * including a role retired from the picker or one from another org type on a historic row. This filter is
 * presentation only: the server validates `role` against the catalog AND the org type on every invite, which
 * is where the gate actually lives.
 */
export function orgRoleOptionsFor(
  orgRoles: readonly SeatRoleOption[] | null | undefined,
  orgType: string | null | undefined,
): readonly SeatRoleOption[] {
  if (!orgRoles || !orgType) return [];
  return orgRoles.filter((r) => r.orgType === orgType && !r.isRetired);
}

/**
 * The band seat titles a picker may OFFER, filtered by the band's plan tier (ART-150) and with the reserved
 * "Artist" title excluded.
 *
 * Two exclusions, both RENDERINGS of served flags, not second implementations of a rule:
 *
 * - `isOwnerOnly` (the "Artist" title, `artist_owner`) is dropped UNCONDITIONALLY, whatever the tier. It is
 *   the owner account's title, minted only by `core.create_band`, and can never be assigned through the crew
 *   pickers — the band seat gates (`accept_band_invite` / `change_band_seat_role`) refuse it and
 *   `RequireBandTitleAsync` refuses it on the offer side. So the offered list and the accepted list agree.
 * - `requiresPro` (the 15 Pro crew titles) is dropped on a Standard band only.
 *
 * The catalog is still served WHOLE so a LABEL resolves for any stored key — including the owner's own
 * "Artist" seat and a Pro seat a downgraded band still holds; the gates are the enforcement. Both pickers
 * that call this mount inside a band, so `isPro` is available — the person-scoped catalog route deliberately
 * cannot filter (it has no band tier), which is why both flags are served rather than filtered out there.
 */
export function bandRoleOptionsFor(
  bandRoles: readonly SeatRoleOption[] | null | undefined,
  isPro: boolean,
): readonly SeatRoleOption[] {
  if (!bandRoles) return [];
  const selectable = bandRoles.filter((r) => !r.isOwnerOnly && !r.isRetired);
  return isPro ? selectable : selectable.filter((r) => !r.requiresPro);
}

/**
 * The roles a FOUNDER may pick when creating an org — admin tier only.
 *
 * This is a lockout guard rather than a security one, and the distinction matters for where it lives: at
 * creation the founder is the only member of the org, so there is nothing to escalate against. What it
 * prevents is someone choosing "Marketing Intern" and creating an organization they cannot administer,
 * invite to, or assign artists in, with nobody else in it to fix it. `core.create_org` re-checks and is the
 * actual guard; this is the affordance.
 */
export function founderRoleOptionsFor(
  orgRoles: readonly SeatRoleOption[] | null | undefined,
  orgType: string | null | undefined,
): readonly SeatRoleOption[] {
  return orgRoleOptionsFor(orgRoles, orgType).filter((r) => r.tier === "admin");
}
